import { GetSubmissionStepperQuery, SubmissionStepType } from "generated-graphql/graphql";
import { SubmissionStepState } from "hooks/useSubmissionSteps";
import { createContext } from "react";

type StepperSubmission = NonNullable<GetSubmissionStepperQuery["submission"]>;

export type WasteStepperContextValue = {
  submissionId: string;
  facilityId: string;
  /**
   * Null only while the shell is still loading. The shell renders skeletons (not
   * the `<Outlet/>`) until it resolves, so step routes/editors reading this via
   * `useWasteStepper()` always see a resolved submission.
   */
  submission: StepperSubmission | null;
  /**
   * Refetches the canonical stepper query from the API. External systems such
   * as BoldSign update submission state outside Apollo's mutation lifecycle,
   * so step routes use this to reconcile those updates into the client cache.
   */
  refetchSubmission: () => Promise<void>;
  stepStates: Partial<Record<SubmissionStepType, SubmissionStepState>>;
  /**
   * True once the submission is frozen (`isSubmissionFrozen`): the snapshot has
   * been taken and the stepper is read-only. Step editors hide their
   * edit/continue affordances and the frozen snapshot view is rendered instead
   * of live tables.
   */
  isFrozen: boolean;
  /** Rejected and not yet routed. `canEdit` folds this in; exposed for the actions that don't. */
  isRejected: boolean;
  /**
   * True when the current user may edit this submission's data. Gated on the
   * RCRA facility write permission (`WriteFacilityRcra`) — the same check the
   * API's stepper mutations authorize against — so customers with that
   * permission edit the stepper just like staff. Frozen or rejected-but-unrouted
   * is read-only for everyone regardless.
   */
  canEdit: boolean;
  /**
   * Opens the Submission Details edit dialog from anywhere in the stepper. The
   * dialog itself stays in `DetailsCard` (which derives its inputs); this is the
   * signal that lets a validation alert offer the fix instead of leaving the
   * user to find the pencil. Increments on each request so repeat asks reopen it.
   */
  requestDetailsEdit: () => void;
  detailsEditRequest: number;
};

/**
 * Raw context object. Consumers should read it via `useWasteStepper()` from
 * `hooks/useWasteStepper`; it is exported here only so that hook can subscribe to
 * it. Kept in this (component-free) module so `WasteStepperContext.tsx`'s only
 * export is the `WasteStepperProvider` component, satisfying
 * `react-refresh/only-export-components`.
 */
export const WasteStepperContext = createContext<WasteStepperContextValue | undefined>(undefined);
